import { PenLine, Printer } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { FirmSignaturesAPI } from "@/features/clientPortal/api";
import {
  refreshClientPortal,
  useFirmPortalCases,
  useFirmPortalState
} from "@/features/clientPortal/queries";
import type { SignatureKind } from "@/features/clientPortal/types";
import type { ForeignNationalDocument } from "@/features/foreignNationalDocuments/types";
import { cn } from "@/lib/utils";

import {
  defaultTitle,
  formatPages,
  parsePages,
  suggestKind
} from "./signingModel";

const NO_CASE = "none";

type Detected = {
  page_count: number;
  pages: { page: number; label: string }[];
};

type Props = {
  clientId: string;
  firstName: string;
  document: ForeignNationalDocument | null;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
};

const KindOption: FC<{
  value: SignatureKind;
  current: SignatureKind;
  title: string;
  body: string;
  icon: typeof PenLine;
}> = ({ value, current, title, body, icon: Icon }) => (
  <Label
    htmlFor={`sign-kind-${value}`}
    className={cn(
      "flex cursor-pointer items-start gap-3 rounded-lg border p-3 font-normal",
      current === value ? "border-primary bg-primary/5" : "border-border"
    )}
  >
    <RadioGroupItem
      id={`sign-kind-${value}`}
      value={value}
      className="mt-0.5"
    />
    <span className="flex flex-col gap-0.5">
      <span className="flex items-center gap-1.5 text-sm font-medium">
        <Icon className="size-3.5" />
        {title}
      </span>
      <span className="text-xs text-muted-foreground">{body}</span>
    </span>
  </Label>
);

/** The firm sending one of the client's PDFs to sign in the portal. */
export const RequestSignatureDialog: FC<Props> = ({
  clientId,
  firstName,
  document,
  onOpenChange,
  onSent
}) => {
  const open = Boolean(document);
  const cases = useFirmPortalCases(open ? clientId : "");
  const portal = useFirmPortalState(open ? clientId : "");
  const [kind, setKind] = useState<SignatureKind>("esign");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [caseId, setCaseId] = useState(NO_CASE);
  const [pagesText, setPagesText] = useState("");
  const [ready, setReady] = useState(true);
  const [detected, setDetected] = useState<Detected | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!document) return undefined;
    let live = true;
    setTitle(defaultTitle(document.label ?? "", document.filename));
    setMessage("");
    setReady(true);
    setDetected(null);
    setPagesText("");
    setKind(suggestKind(document.filename, 0));
    FirmSignaturesAPI.signaturePages(
      clientId,
      document.foreign_national_document_id
    )
      .then((found) => {
        if (!live) return;
        setDetected(found);
        setPagesText(formatPages(found.pages.map((p) => p.page)));
        setKind(suggestKind(document.filename, found.pages.length));
      })
      .catch(() => live && setDetected({ page_count: 0, pages: [] }));
    return () => {
      live = false;
    };
  }, [clientId, document]);

  const caseList = cases.data?.cases ?? [];
  useEffect(() => {
    const list = cases.data?.cases ?? [];
    setCaseId(list.length === 1 ? list[0].id : NO_CASE);
  }, [cases.data, document]);

  const pages = parsePages(pagesText, detected?.page_count || null);
  const portalOff = portal.data?.state === "off";
  const canSend =
    Boolean(title.trim()) && (kind === "esign" || pages.length > 0) && !busy;

  const send = () => {
    if (!document) return;
    setBusy(true);
    FirmSignaturesAPI.create(clientId, {
      document_id: document.foreign_national_document_id,
      kind,
      title: title.trim(),
      message: message.trim() || undefined,
      individual_id: caseId === NO_CASE ? null : caseId,
      pages: kind === "ink" ? pages : undefined,
      ready
    })
      .then((created) => {
        toast.success(
          !ready
            ? `Added. ${firstName} sees it as “Not yet” until you send it.`
            : created.email_sent
            ? `Sent. We emailed ${firstName} a link to sign.`
            : `Ready in ${firstName}'s portal.`
        );
        onOpenChange(false);
        onSent?.();
        return refreshClientPortal();
      })
      .catch((e: unknown) =>
        toast.error("Couldn't send it for signing.", {
          description: e instanceof Error ? e.message : undefined
        })
      )
      .finally(() => setBusy(false));
  };

  const labels = new Map(detected?.pages.map((p) => [p.page, p.label]) ?? []);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Ask {firstName} to sign</DialogTitle>
          <DialogDescription className="truncate">
            {document?.filename}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <RadioGroup
            value={kind}
            onValueChange={(v) => setKind(v as SignatureKind)}
            className="grid gap-2 sm:grid-cols-2"
          >
            <KindOption
              value="esign"
              current={kind}
              icon={PenLine}
              title="On screen"
              body="Engagement letters, declarations, support-letter acknowledgements."
            />
            <KindOption
              value="ink"
              current={kind}
              icon={Printer}
              title="In ink"
              body="USCIS paper forms. They print, sign, and send a photo."
            />
          </RadioGroup>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sign-title">What {firstName} sees</Label>
            <Input
              id="sign-title"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {kind === "ink" ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sign-pages">Pages to sign</Label>
              <Input
                id="sign-pages"
                value={pagesText}
                placeholder={detected ? "e.g. 6" : "Finding signature pages…"}
                onChange={(e) => setPagesText(e.target.value)}
              />
              <span className="text-xs text-muted-foreground">
                {pages.length
                  ? pages
                      .map((p) =>
                        labels.get(p)
                          ? `Page ${p}: ${labels.get(p)}`
                          : `Page ${p}`
                      )
                      .join(" · ")
                  : detected && !detected.pages.length
                  ? "No applicant or petitioner signature box found. Enter the pages."
                  : null}
              </span>
              <span className="text-xs text-muted-foreground">
                {firstName} prints only these pages. The PDF itself isn't
                marked.
              </span>
            </div>
          ) : null}

          {caseList.length ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sign-case">File the signed copy to</Label>
              <Select value={caseId} onValueChange={setCaseId}>
                <SelectTrigger id="sign-case">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CASE}>Client Documents Only</SelectItem>
                  {caseList.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sign-message">Note to {firstName} (optional)</Label>
            <Textarea
              id="sign-message"
              rows={2}
              value={message}
              maxLength={1000}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
            <span className="flex flex-col gap-0.5">
              <Label htmlFor="sign-ready">Send now</Label>
              <span className="text-xs text-muted-foreground">
                {ready
                  ? `${firstName} gets an email and can sign right away.`
                  : `${firstName} sees it as “Not yet” until you send it from Overview.`}
              </span>
            </span>
            <Switch
              id="sign-ready"
              checked={ready}
              onCheckedChange={setReady}
            />
          </div>

          {portalOff ? (
            <p className="rounded-lg bg-warn-soft px-3 py-2 text-sm text-warn">
              {firstName} doesn't have portal access yet. Invite them from
              Overview so they can sign.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSend}
            onClick={send}
            data-testid="signature-request-send"
          >
            {busy
              ? "Sending…"
              : ready
              ? "Send for signature"
              : "Add as not yet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
